// ============================================
// APP.JS - Application principale
// ============================================

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ScrollToTop from "./components/ScrollToTop";

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import CandidateDashboard from './pages/CandidateDashboard';
import Announcements from './pages/Announcements';

import Concours from './pages/Concours';
import MentionsLegales from './pages/MentionsLegales';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import Accessibilite from './pages/Accessibilite';

import Ecole from './pages/Ecole';
import Mediatheque from './pages/Mediatheque';
import Photos from './pages/Photos';
import Formations from "./pages/Formations";
import JugesEma from "./pages/JugesEma";
import MaguyFlorestal from "./pages/gouvernance/MaguyFlorestal";
import JeanneBeanneCasty from "./pages/gouvernance/JeanneBeanneCasty";
import KesnerMichelThermesi from "./pages/gouvernance/KesnerMichelThermesi";
import Presentation from "./pages/ecole/Presentation";
import Historique from "./pages/ecole/Historique";
import Mission from "./pages/ecole/Mission";
import Gouvernance from "./pages/ecole/Gouvernance";
import Organigramme from "./pages/ecole/Organigramme";
import Temoignages from "./pages/ecole/Temoignages";
import Realisations from "./pages/ecole/Realisations";
import Chiffres from "./pages/ecole/Chiffres";
import ProcesFictifs from "./pages/ProcesFictifs";
import PrestationSerment8e from "./pages/actualites/PrestationSerment8e";

// Admin
import { AdminAuthProvider } from "./contexts/AuthContext";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminPrivateRoute from "./components/PrivateRoute";

// Thème personnalisé
const theme = createTheme({
    palette: {
        primary: {
            main: '#003366',
        },
        secondary: {
            main: '#FFD700',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Arial", sans-serif',
    },
});

// Composant pour protéger les routes (nécessite authentification)
const PrivateRoute = ({ children }) => {
    const { user } = useAuth();
    return user ? children : <Navigate to="/login" />;
};

// Composant pour rediriger si déjà connecté
const PublicRoute = ({ children }) => {
    const { user } = useAuth();
    return !user ? children : <Navigate to="/dashboard" />;
};

function AppContent() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />
            <Box component="main" sx={{ flexGrow: 1 }}>
                <Routes>
                    {/* Routes publiques */}
                    <Route path="/" element={<Home />} />
                    <Route path="/announcements" element={<Announcements />} />

                    {/* Nouvelles routes publiques (institutionnelles) */}
                    <Route path="/concours" element={<Concours />} />
                    <Route path="/mentions-legales" element={<MentionsLegales />} />
                    <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                    <Route path="/accessibilite" element={<Accessibilite />} />

                    <Route path="/ecole" element={<Ecole />} />
                    <Route path="/ecole/presentation" element={<Presentation />} />
                    <Route path="/ecole/historique" element={<Historique />} />
                    <Route path="/ecole/mission" element={<Mission />} />
                    <Route path="/ecole/gouvernance" element={<Gouvernance />} />
                    <Route path="/ecole/organigramme" element={<Organigramme />} />
                    <Route path="/ecole/temoignages" element={<Temoignages />} />
                    <Route path="/ecole/realisations" element={<Realisations />} />
                    <Route path="/ecole/chiffres" element={<Chiffres />} />
                    <Route path="/mediatheque" element={<Mediatheque />} />
                    <Route path="/mediatheque/photos" element={<Photos />} />
                    <Route path="/formations" element={<Formations />} />
                    <Route path="/proces-fictifs" element={<ProcesFictifs />} />
                    <Route path="/actualites/prestation-serment-8e-promotion" element={<PrestationSerment8e />} />
                    <Route path="/magistrats-ema" element={<JugesEma />} />
                    <Route path="/gouvernance/maguy-florestal" element={<MaguyFlorestal />} />
                    <Route path="/gouvernance/jeanne-beanne-casty" element={<JeanneBeanneCasty />} />
                    <Route path="/gouvernance/kesner-michel-thermesi" element={<KesnerMichelThermesi />} />

                    {/* Routes publiques (redirection si connecté) */}
                    <Route
                        path="/login"
                        element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            <PublicRoute>
                                <Register />
                            </PublicRoute>
                        }
                    />

                    {/* Routes protégées (nécessite authentification) */}
                    <Route
                        path="/dashboard"
                        element={
                            <PrivateRoute>
                                <CandidateDashboard />
                            </PrivateRoute>
                        }
                    />

                    {/* Routes Admin */}
                    <Route path="/admin" element={
                        <AdminAuthProvider>
                            <AdminLogin />
                        </AdminAuthProvider>
                    } />
                    <Route path="/admin/dashboard" element={
                        <AdminAuthProvider>
                            <AdminPrivateRoute>
                                <AdminDashboard />
                            </AdminPrivateRoute>
                        </AdminAuthProvider>
                    } />

                    {/* Route par défaut (404) */}
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </Box>
            <Footer />

            {/* Notifications Toast */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </Box>
    );
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <ScrollToTop />
                <AuthProvider>
                    <AppContent />
                </AuthProvider>
            </Router>
        </ThemeProvider>
    );
}

export default App;